import Swiper from '@app/domains/common/components/Swiper';
import useInViewForPosthog from '@app/domains/common/hooks/useInViewForPosthog';
import useSiteAnnouncements from '@app/domains/common/hooks/useSiteAnnouncements';
import { HomeCollectionSlots } from '@app/domains/home/analytics/posthog/constants';
import { HomeEvents } from '@app/domains/home/analytics/posthog/events';
import HomeSiteAnnouncement from '@app/domains/home/components/HomeSiteAnnouncement';
import HomeSiteAnnouncementSkeleton from '@app/domains/home/components/HomeSiteAnnouncement/HomeSiteAnnouncementSkeleton';
import HomeSwiperController from '@app/domains/home/components/HomeSwiperController';
import useHomeMediaQuery from '@app/domains/home/hooks/useHomeMediaQuery';
import {
    HOME_SWIPER_PAGINATION_ACTIVE_BULLET_STYLE,
    HOME_SWIPER_PAGINATION_BULLET_STYLE,
} from '@app/domains/home/styles';
import { Suspense, useMemo } from 'react';
import { Link } from 'react-router-dom';

function HomeSiteAnnouncements() {
    const { data: siteAnnouncements = [] } = useSiteAnnouncements();

    const { isMobile, isTablet, isDesktop, isLargeScreen, isUpMd } = useHomeMediaQuery();

    const slidesPerView = (() => {
        if (isMobile) return 1;
        if (isTablet) return 2;
        if (isDesktop) return 2;
        if (isLargeScreen) return 1;
        return 1;
    })();

    const spaceBetween = (() => {
        if (isMobile) return 8;
        if (isTablet) return 12;
        if (isDesktop) return 16;
        if (isLargeScreen) return 20;
        return 20;
    })();

    const siteAnnouncementsPerSlide = (() => {
        if (isMobile) return 1;
        if (isTablet) return 1;
        if (isDesktop) return 1;
        if (isLargeScreen) return 2;
        return 2;
    })();

    const slideSiteAnnouncementsArray = useMemo(
        () =>
            Array.from(Array(siteAnnouncements.length / siteAnnouncementsPerSlide), (_, i) =>
                siteAnnouncements.slice(i * siteAnnouncementsPerSlide, (i + 1) * siteAnnouncementsPerSlide)
            ),
        [siteAnnouncements, siteAnnouncementsPerSlide]
    );

    return (
        <Swiper
            style={!isLargeScreen ? { overflow: 'visible' } : {}}
            slidesPerView={slidesPerView}
            slidesPerGroup={slidesPerView}
            spaceBetween={spaceBetween}
            navigation={isUpMd}
            pagination={{
                clickable: isUpMd,
                bulletClass: HOME_SWIPER_PAGINATION_BULLET_STYLE,
                bulletActiveClass: HOME_SWIPER_PAGINATION_ACTIVE_BULLET_STYLE,
            }}
        >
            {slideSiteAnnouncementsArray.map((slideSiteAnnouncementItems, slideIndex) => (
                <Swiper.Slide key={slideIndex} data-posthog-params={JSON.stringify({ itemSlot: slideIndex })}>
                    <div className="flex flex-row gap-8 sm:gap-12 md:gap-16 lg:flex-col lg:gap-0 lg:space-y-16 -md:[&_>:nth-child(1n+2)]:hidden">
                        {slideSiteAnnouncementItems.map(siteAnnouncementItem => (
                            <HomeSiteAnnouncement
                                key={siteAnnouncementItem.entityId}
                                siteAnnouncement={siteAnnouncementItem}
                            />
                        ))}
                    </div>
                </Swiper.Slide>
            ))}
            <HomeSwiperController className="h-48 lg:mt-14">
                {isUpMd && (
                    <>
                        <HomeSwiperController.PrevButton />
                        <HomeSwiperController.Pages />
                        <HomeSwiperController.NextButton />
                    </>
                )}
            </HomeSwiperController>
        </Swiper>
    );
}

export default function HomeSiteAnnouncementsWrapper() {
    const { ref } = useInViewForPosthog({
        event: HomeEvents.ViewCollection,
        triggerOnce: true,
    });

    return (
        <article ref={ref} data-posthog-params={JSON.stringify({ collectionSlot: HomeCollectionSlots.Announcement })}>
            <div className="mb-12 flex items-center md:mb-16">
                <h3 className="font-set-b18 text-gray-t1 md:font-set-b24">Announcements</h3>
                <Link className="font-set-m14 ml-auto text-gray-700 md:font-set-m16 dark:text-gray-550" to="/news">
                    View All
                </Link>
            </div>
            <Suspense
                fallback={
                    <div className="flex flex-row gap-12 md:pb-48 lg:flex-col lg:gap-16 lg:pb-62">
                        <HomeSiteAnnouncementSkeleton />
                        <HomeSiteAnnouncementSkeleton className="hidden sm2:block" />
                    </div>
                }
            >
                <HomeSiteAnnouncements />
            </Suspense>
        </article>
    );
}
